import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'react-hot-toast';
import CustomDatePicker from '@/components/shared/CustomDatePicker';
import SearchableSelect from '@/components/ui/SearchableSelect';

export default function ManualAttendanceModal({ isOpen, onClose, teamMembers = [], initialData = null }) {
  const queryClient = useQueryClient();
  
  const [form, setForm] = useState({
    employee_id: '',
    date: new Date().toISOString().split('T')[0],
    time: '08:00',
    type: 'check_in',
    area: 'Office',
    notes: ''
  });

  React.useEffect(() => {
    if (isOpen) {
      setForm({
        employee_id: initialData?.employee_id || '',
        date: initialData?.date || new Date().toISOString().split('T')[0],
        time: initialData?.time || '17:00',
        type: initialData?.type || 'check_in',
        area: 'Office',
        notes: initialData?.notes || ''
      });
    }
  }, [isOpen, initialData]);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const submitMutation = useMutation({
    mutationFn: async () => {
      const selectedMember = teamMembers.find(m => m.id === form.employee_id);
      if (!selectedMember) throw new Error("Karyawan tidak ditemukan");

      // Construct ISO string for timestamp
      // Format: YYYY-MM-DDTHH:mm:00
      const timestampIso = `${form.date}T${form.time}:00`;

      let finalNotes = '';
      if (form.area) {
        finalNotes = `Area: ${form.area}`;
      }
      if (form.notes) {
        finalNotes = finalNotes ? `${finalNotes} | ${form.notes}` : form.notes;
      }

      const newLog = {
        employee_id: selectedMember.id,
        full_name: selectedMember.full_name || selectedMember.username,
        timestamp: timestampIso,
        type: form.type,
        method: 'manual_by_admin',
        notes: finalNotes
      };

      return await base44.entities.AttendanceLog.create(newLog);
    },
    onSuccess: () => {
      toast.success("Absensi manual berhasil ditambahkan!");
      queryClient.invalidateQueries(["attendance-logs-all"]);
      queryClient.invalidateQueries(["attendance-logs"]);
      onClose();
      // Reset form
      setForm({
        employee_id: '',
        date: new Date().toISOString().split('T')[0],
        time: '08:00',
        type: 'check_in',
        notes: ''
      });
    },
    onError: (err) => {
      console.error(err);
      toast.error("Gagal menyimpan absensi manual.");
    }
  });

  const handleSave = () => {
    if (!form.employee_id || !form.date || !form.time) {
      toast.error("Harap isi Karyawan, Tanggal, dan Waktu.");
      return;
    }
    submitMutation.mutate();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Input Absen Manual</DialogTitle>
          <DialogDescription className="text-xs">
            Gunakan form ini untuk membantu absen karyawan yang mengalami kendala teknis (HP rusak, lupa tekan absen, dll).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          <div className="space-y-1">
            <Label className="text-xs">Karyawan *</Label>
            <SearchableSelect
              value={form.employee_id}
              onValueChange={(v) => set('employee_id', v)}
              placeholder={teamMembers.length === 0 ? "Tidak ada staf ditemukan (Cek koneksi DB/Manajemen Tim)" : "Pilih Karyawan..."}
              options={teamMembers.map(m => ({
                value: m.id,
                label: `${m.full_name || m.username} (${m.employee_id || 'No ID'})`
              }))}
              disabled={teamMembers.length === 0}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Tanggal *</Label>
              <CustomDatePicker 
                value={form.date} 
                onChange={(val) => set('date', typeof val === 'string' ? val : (val?.target?.value || ''))} 
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Waktu (Jam) *</Label>
              <Input 
                type="time" 
                value={form.time} 
                onChange={(e) => set('time', e.target.value)} 
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Tipe Absen *</Label>
              <Select value={form.type} onValueChange={(v) => set('type', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  <SelectItem value="check_in">Check In (Masuk)</SelectItem>
                  <SelectItem value="check_out">Check Out (Pulang)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Area Lokasi</Label>
              <Select value={form.area} onValueChange={(v) => set('area', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Pilih area..." />
                </SelectTrigger>
                <SelectContent className="z-[100]">
                  <SelectItem value="Lahan Parkir">🅿️ Lahan Parkir</SelectItem>
                  <SelectItem value="Office">🏢 Office</SelectItem>
                  <SelectItem value="Pabrik Lantai 1">🏭 Pabrik Lantai 1</SelectItem>
                  <SelectItem value="Pabrik Lantai 2">🏗️ Pabrik Lantai 2</SelectItem>
                  <SelectItem value="Depan Gerbang">🚪 Depan Gerbang</SelectItem>
                  <SelectItem value="Dinas Luar">🚗 Dinas Luar</SelectItem>
                  <SelectItem value="Lainnya">📍 Lainnya</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Catatan / Alasan</Label>
            <Textarea 
              value={form.notes} 
              onChange={(e) => set('notes', e.target.value)} 
              placeholder="Contoh: Lupa klik absen masuk, HP rusak, dll"
              rows={3}
            />
          </div>
        </div>

        <div className="flex gap-2 justify-end mt-4">
          <Button variant="outline" onClick={onClose} disabled={submitMutation.isPending}>
            Batal
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={submitMutation.isPending || !form.employee_id}
            className="bg-[#D4AF37] hover:bg-[#B58B22] text-white"
          >
            {submitMutation.isPending ? "Menyimpan..." : "Simpan Absen"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
